import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { IPageData } from '../shared/components/abstract-page/abstract-page.component';
import { Subject } from 'rxjs';
import { SiteSettingsService } from './site-settings.service';
import { get } from 'lodash';
import { IInitialPageData } from '../shared/interface/general';

@Injectable({
  providedIn: 'root'
})

export class PageResolverService {
  initialPageData: IInitialPageData | null;
  private cache = new Map<string, IPageData>();
  pageDataChanged$ = new Subject();

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private router: Router,
    private siteSettingsService: SiteSettingsService,
  ) {
    const initialPageData = get(document.getElementById('initialPageData'), 'innerHTML');
    this.initialPageData = initialPageData ? JSON.parse(initialPageData) : null;
  }

  async resolve(route: ActivatedRouteSnapshot) {
    const path = `/${route.url.join('/')}`;
    let queryParams = this.getQueryAsString(route.queryParams);
    const key = queryParams.length === 0 ? path : path + queryParams;
    let cached = this.cache.get(key);

    if (!cached) {
      if (this.initialPageData && this.initialPageData.getByUrl) {
        cached = this.parseResponse(this.initialPageData.getByUrl);
        this.initialPageData.getByUrl = undefined;
      } else {
        cached = await this.byPath(path, queryParams);
      }
      const utmParams = this.getUtmParams(cached);
      if (Object.keys(utmParams).length === 0) {
        this.cache.set(key, cached);
      }
    }

    if (!cached) {
      const siteSettings = await this.siteSettingsService.getSiteSettings();
      this.router.navigate([siteSettings.pageNotFoundPageUrl]);
    }

    this.pageDataChanged$.next(cached);

    return cached;
  }

  async byPath(path: string, query: string) {
    const currentHost = this.appConfigService.getHostName().replace(/\/$/, "");
    const host = await this.getHost(currentHost);

    if (currentHost === host) {
      return this.http.get(`ubaseline/api/node/getByurl?url=${host}${path}${query}`).pipe(
        map(res => this.parseResponse(res))
      ).toPromise();
    }
    else {
      window.location.href = host + path + query;
    }
  }

  async getHost(currentHost : string) {
    const culture = this.tryGetCulture();
    if (culture) {
      return this.http.get(`ubaseline/api/domain/getDomainByCulture?culture=${culture}`).pipe(
        map(res => { return (<any>res).replace(/\/$/, ""); })
      ).toPromise();
    }
    else {
      return currentHost.replace(/\/$/, "");
    }
  }

  private tryGetCulture() {
    const cultureHash = "#?culture=";
    return window.location.search.indexOf("preview") > -1 && window.location.hash.startsWith(cultureHash) ?
      window.location.hash.split(cultureHash)[1] :
      "";
  }

  private parseResponse(res: any) {
    if (res === null) return res;
    if (!res.isDefaultModel) return res;

    let data = {};

    res['properties'].forEach(prop => {
      data[prop['alias']] = {
        value: prop['value'],
        editorAlias: prop['editorAlias']
      }
    });

    return data;
  }

  private getUtmParams(data: IPageData) {
    return data && data.utmConfiguration ? data.utmConfiguration.value.params : {};
  }

  private getQueryAsString(queryParams: any) {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return "";
    }

    let query = [];
    Object.keys(queryParams).forEach(function (key) {
      query.push(key + '=' + queryParams[key]);
    });

    return `?${query.join('%26')}`;
  }
}